/* ==========================================================================
   Hurmey theme — scripts (no dependencies)
   ========================================================================== */
(() => {
  'use strict';

  const theme = window.theme || {};
  const routes = theme.routes || {};
  const strings = theme.strings || {};

  /* ---------- Helpers ---------- */
  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn.apply(null, args), wait); };
  };
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;
    const fmt = format || theme.moneyFormat || '€{{amount_with_comma_separator}}';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const withDelimiters = (number, precision, thousands = ',', decimal = '.') => {
      if (isNaN(number) || number == null) return '0';
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    };
    const match = fmt.match(placeholder);
    if (!match) return fmt;
    let value = '';
    switch (match[1]) {
      case 'amount': value = withDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' '); break;
      default: value = withDelimiters(cents, 2);
    }
    return fmt.replace(placeholder, value);
  }
  theme.formatMoney = formatMoney;

  function toast(message) {
    const el = qs('#ThemeToast');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(el._t);
    el._t = setTimeout(() => { el.hidden = true; }, 3200);
  }

  async function fetchJSON(url, options = {}) {
    const res = await fetch(url, {
      ...options,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(options.headers || {}) }
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) {
      const err = new Error(data.description || data.message || strings.cartError || 'Error');
      err.data = data;
      throw err;
    }
    return data;
  }

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* ---------- Cart API ---------- */
  const Cart = {
    sectionIds() {
      const ids = [];
      if (qs('cart-drawer')) ids.push('cart-drawer');
      const page = qs('cart-page');
      if (page && page.dataset.section) ids.push(page.dataset.section);
      return ids;
    },
    async add(items) {
      const sections = this.sectionIds();
      const data = await fetchJSON(routes.cart_add + '.js', {
        method: 'POST',
        body: JSON.stringify({ items, sections, sections_url: window.location.pathname })
      });
      this.render(data.sections);
      await this.refreshCount();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { action: 'add' } }));
      return data;
    },
    async change(line, quantity) {
      const sections = this.sectionIds();
      const data = await fetchJSON(routes.cart_change + '.js', {
        method: 'POST',
        body: JSON.stringify({ line, quantity, sections, sections_url: window.location.pathname })
      });
      this.render(data.sections);
      this.updateCount(data.item_count);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { action: 'change' } }));
      return data;
    },
    async note(note) {
      return fetchJSON(routes.cart_update + '.js', { method: 'POST', body: JSON.stringify({ note }) });
    },
    render(sections) {
      if (!sections) return;
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = parseHTML(html);
        if (id === 'cart-drawer') {
          const drawer = qs('cart-drawer');
          const fresh = qs('cart-drawer', doc);
          if (drawer && fresh) {
            qs('.drawer__panel', drawer).innerHTML = qs('.drawer__panel', fresh).innerHTML;
            drawer.classList.toggle('is-empty', fresh.classList.contains('is-empty'));
          }
        } else {
          const page = qs(`cart-page[data-section="${id}"]`);
          const fresh = qs('cart-page', doc);
          if (page && fresh) page.innerHTML = fresh.innerHTML;
        }
      });
    },
    async refreshCount() {
      try {
        const cart = await fetchJSON(routes.cart + '.js');
        this.updateCount(cart.item_count);
      } catch (e) { /* ignore */ }
    },
    updateCount(count) {
      const bubble = qs('#cart-icon-bubble');
      if (!bubble) return;
      let el = qs('.cart-count', bubble);
      if (count > 0) {
        if (!el) {
          el = document.createElement('span');
          el.className = 'cart-count';
          el.setAttribute('aria-hidden', 'true');
          bubble.appendChild(el);
        }
        el.textContent = count;
      } else if (el) {
        el.remove();
      }
    }
  };
  theme.Cart = Cart;

  /* ---------- Drawers ---------- */
  class DrawerComponent extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]')) { e.preventDefault(); this.close(); }
      });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    }
    open(trigger) {
      this._trigger = trigger || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      const panel = qs('.drawer__panel', this);
      setTimeout(() => {
        const input = qs('input[type="search"]', this);
        (input || panel).focus({ preventScroll: true });
      }, 60);
    }
    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      if (!qs('.drawer.is-open')) document.body.classList.remove('overflow-hidden');
      if (this._trigger && this._trigger.focus) this._trigger.focus({ preventScroll: true });
    }
  }
  define('drawer-component', DrawerComponent);

  class CartDrawer extends DrawerComponent {
    connectedCallback() {
      super.connectedCallback();
      this.addEventListener('change', (e) => {
        const input = e.target.closest('.quantity__input[data-line]');
        if (input) this.updateLine(input.dataset.line, input.value, input.closest('.cart-item'));
        const note = e.target.closest('[data-cart-note]');
        if (note) Cart.note(note.value);
      });
      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-cart-remove]');
        if (remove) { e.preventDefault(); this.updateLine(remove.dataset.cartRemove, 0, remove.closest('.cart-item')); }
      });
    }
    async updateLine(line, qty, row) {
      if (row) row.classList.add('is-loading');
      try { await Cart.change(parseInt(line, 10), parseInt(qty, 10)); }
      catch (err) { toast(err.message); if (row) row.classList.remove('is-loading'); }
    }
  }
  define('cart-drawer', CartDrawer);

  class CartPage extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (e) => {
        const input = e.target.closest('.quantity__input[data-line]');
        if (input) this.updateLine(input.dataset.line, input.value, input.closest('.cart-item'));
        const note = e.target.closest('[data-cart-note]');
        if (note) Cart.note(note.value);
      });
      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-cart-remove]');
        if (remove) { e.preventDefault(); this.updateLine(remove.dataset.cartRemove, 0, remove.closest('.cart-item')); }
      });
    }
    async updateLine(line, qty, row) {
      if (row) row.classList.add('is-loading');
      try { await Cart.change(parseInt(line, 10), parseInt(qty, 10)); }
      catch (err) { toast(err.message); if (row) row.classList.remove('is-loading'); }
    }
  }
  define('cart-page', CartPage);

  // Open drawers from any trigger
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      const drawer = document.getElementById(opener.dataset.drawerOpen);
      if (drawer && drawer.open) { e.preventDefault(); drawer.open(opener); }
      return;
    }
    const cartToggle = e.target.closest('[data-cart-toggle]');
    if (cartToggle) {
      const drawer = qs('cart-drawer');
      if (drawer && theme.cartType === 'drawer') { e.preventDefault(); drawer.open(cartToggle); }
    }
  });

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = qs('input', this);
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-qty-change]');
        if (!btn) return;
        e.preventDefault();
        const step = parseInt(btn.dataset.qtyChange, 10);
        const min = parseInt(this.input.min || '0', 10);
        const next = Math.max(min, (parseInt(this.input.value, 10) || 0) + step);
        if (next === parseInt(this.input.value, 10)) return;
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  define('quantity-input', QuantityInput);

  /* ---------- Add to cart (all product forms) ---------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form.js-product-form');
    if (!form) return;
    e.preventDefault();

    const submitter = e.submitter || qs('[type="submit"]', form);
    const errorBox = qs('[data-form-error]', form);
    if (errorBox) { errorBox.hidden = true; errorBox.textContent = ''; }
    if (submitter) { submitter.classList.add('is-loading'); submitter.setAttribute('aria-disabled', 'true'); }

    const fd = new FormData(form);
    const item = { id: parseInt(fd.get('id'), 10), quantity: parseInt(fd.get('quantity') || '1', 10) || 1 };
    const plan = fd.get('selling_plan');
    if (plan) item.selling_plan = parseInt(plan, 10);
    const properties = {};
    for (const [key, value] of fd.entries()) {
      const m = key.match(/^properties\[(.+)\]$/);
      if (m && value) properties[m[1]] = value;
    }
    if (Object.keys(properties).length) item.properties = properties;
    const items = [item];

    // Free gifts (main product form only)
    if (form.hasAttribute('data-main-form')) {
      const scope = form.closest('product-info') || document;
      const gifts = qs('[data-gifts]', scope);
      if (gifts && gifts.dataset.autoAdd === 'true') {
        let inCart = [];
        try {
          const current = await fetchJSON(routes.cart + '.js');
          inCart = current.items.filter((i) => i.properties && i.properties._gift).map((i) => i.product_id);
        } catch (e) { /* ignore */ }
        qsa('[data-gift]', gifts).forEach((card) => {
          const input = qs('[data-gift-variant]', card);
          const id = input ? parseInt(input.value, 10) : 0;
          const productId = parseInt(card.dataset.productId, 10);
          if (id && !inCart.includes(productId)) items.push({ id, quantity: 1, properties: { _gift: 'true' } });
        });
      }
    }

    try {
      await Cart.add(items);
      const drawer = qs('cart-drawer');
      if (drawer && theme.cartType === 'drawer') {
        drawer.open(submitter);
      } else {
        window.location.href = routes.cart;
      }
    } catch (err) {
      if (errorBox) { errorBox.textContent = err.message; errorBox.hidden = false; }
      else toast(err.message);
    } finally {
      if (submitter) { submitter.classList.remove('is-loading'); submitter.removeAttribute('aria-disabled'); }
    }
  });

  /* ---------- Announcement bar ---------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.slides = qsa('[data-slide]', this);
      this.index = 0;
      if (this.slides.length < 2) return;
      qs('[data-prev]', this)?.addEventListener('click', () => { this.go(this.index - 1); this.restart(); });
      qs('[data-next]', this)?.addEventListener('click', () => { this.go(this.index + 1); this.restart(); });
      this.delay = parseInt(this.dataset.autoplay, 10) * 1000;
      this.restart();
      this.addEventListener('mouseenter', () => clearInterval(this.timer));
      this.addEventListener('mouseleave', () => this.restart());
    }
    restart() {
      clearInterval(this.timer);
      if (this.delay > 0) this.timer = setInterval(() => this.go(this.index + 1), this.delay);
    }
    go(i) {
      this.index = (i + this.slides.length) % this.slides.length;
      this.slides.forEach((s, n) => s.classList.toggle('is-active', n === this.index));
    }
  }
  define('announcement-bar', AnnouncementBar);

  /* ---------- Countdown (counts down to a real end date) ---------- */
  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      this.end = Date.parse(this.dataset.end);
      if (isNaN(this.end)) { this.hidden = true; return; }
      this.els = {
        d: qs('[data-days]', this), dSep: qs('[data-days-sep]', this),
        h: qs('[data-hours]', this), m: qs('[data-minutes]', this), s: qs('[data-seconds]', this)
      };
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    tick() {
      let diff = Math.max(0, Math.floor((this.end - Date.now()) / 1000));
      if (diff === 0) {
        clearInterval(this.timer);
        if (this.dataset.hideOnEnd === 'true') this.hidden = true;
      }
      const d = Math.floor(diff / 86400); diff -= d * 86400;
      const h = Math.floor(diff / 3600); diff -= h * 3600;
      const m = Math.floor(diff / 60); const s = diff - m * 60;
      const pad = (n) => String(n).padStart(2, '0');
      const showDays = d > 0;
      this.els.d.hidden = !showDays; this.els.dSep.hidden = !showDays;
      this.els.d.textContent = d + 'd';
      this.els.h.textContent = pad(h);
      this.els.m.textContent = pad(m);
      this.els.s.textContent = pad(s);
    }
  }
  define('countdown-timer', CountdownTimer);

  /* ---------- Sticky header ---------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.sticky;
      let last = window.scrollY;
      const onScroll = () => {
        const y = window.scrollY;
        this.classList.toggle('is-scrolled', y > 10);
        if (this.mode === 'scroll-up') {
          const goingDown = y > last && y > 200;
          this.classList.toggle('is-hidden', goingDown && !qs('details[open]', this));
        }
        last = y;
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();

      // Dropdowns: hover on desktop, one open at a time, close on outside click
      const dropdowns = qsa('details.js-dropdown', this);
      dropdowns.forEach((d) => {
        const item = d.closest('.menu-list__item');
        item.addEventListener('mouseenter', () => { if (window.matchMedia('(hover: hover)').matches) d.open = true; });
        item.addEventListener('mouseleave', () => { if (window.matchMedia('(hover: hover)').matches) d.open = false; });
        d.addEventListener('toggle', () => { if (d.open) dropdowns.forEach((o) => { if (o !== d) o.open = false; }); });
      });
      document.addEventListener('click', (e) => {
        if (!e.target.closest('details.js-dropdown')) dropdowns.forEach((d) => { d.open = false; });
      });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') dropdowns.forEach((d) => { d.open = false; }); });
    }
  }
  define('sticky-header', StickyHeader);

  /* ---------- Predictive search ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = qs('[data-search-input]', this);
      this.results = qs('[data-search-results]', this);
      if (this.dataset.enabled !== 'true' || !this.input) return;
      this.input.addEventListener('input', debounce(() => this.search(), 260));
    }
    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) { this.results.innerHTML = ''; return; }
      const url = `${routes.predictive_search}?q=${encodeURIComponent(q)}&resources[type]=product,collection,page,article&resources[limit]=6&section_id=predictive-search`;
      try {
        const res = await fetch(url);
        const html = await res.text();
        const doc = parseHTML(html);
        const section = qs('.shopify-section', doc) || doc.body;
        this.results.innerHTML = section.innerHTML;
      } catch (e) { /* ignore */ }
    }
  }
  define('predictive-search', PredictiveSearch);

  /* ---------- Tabs ---------- */
  class TabGroup extends HTMLElement {
    connectedCallback() {
      this.tabs = qsa(':scope > .pill-tabs [role="tab"]', this);
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.select(tab));
        tab.addEventListener('keydown', (e) => {
          const i = this.tabs.indexOf(tab);
          if (e.key === 'ArrowRight') { e.preventDefault(); this.select(this.tabs[(i + 1) % this.tabs.length], true); }
          if (e.key === 'ArrowLeft') { e.preventDefault(); this.select(this.tabs[(i - 1 + this.tabs.length) % this.tabs.length], true); }
        });
      });
    }
    select(tab, focus) {
      this.tabs.forEach((t) => {
        const selected = t === tab;
        t.setAttribute('aria-selected', selected);
        t.tabIndex = selected ? 0 : -1;
        const panel = document.getElementById(t.getAttribute('aria-controls'));
        if (panel) {
          panel.hidden = !selected;
          if (selected) qsa('.reveal', panel).forEach((el) => el.classList.add('is-visible'));
        }
      });
      if (focus) tab.focus();
      tab.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
    }
  }
  define('tab-group', TabGroup);

  /* ---------- Media gallery ---------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = qs('[data-slides]', this);
      this.slides = qsa('.gallery__slide', this);
      this.thumbs = qsa('[data-thumb-index]', this);
      this.counter = qs('[data-gallery-current]', this);
      qs('[data-gallery-prev]', this)?.addEventListener('click', () => this.go(this.current() - 1));
      qs('[data-gallery-next]', this)?.addEventListener('click', () => this.go(this.current() + 1));
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(parseInt(t.dataset.thumbIndex, 10))));
      this.track.addEventListener('scroll', debounce(() => this.sync(), 60), { passive: true });
      const active = this.slides.findIndex((s) => s.hasAttribute('data-active'));
      if (active > 0) requestAnimationFrame(() => this.go(active, false));
    }
    current() {
      return Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
    }
    go(i, smooth = true) {
      if (!this.slides.length) return;
      const index = (i + this.slides.length) % this.slides.length;
      this.track.scrollTo({ left: index * this.track.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
      this.mark(index);
    }
    goToMedia(mediaId) {
      const index = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (index >= 0) this.go(index);
    }
    sync() { this.mark(this.current()); }
    mark(index) {
      this.thumbs.forEach((t, n) => t.classList.toggle('is-active', n === index));
      if (this.counter) this.counter.textContent = index + 1;
      const thumb = this.thumbs[index];
      if (thumb) {
        const list = thumb.closest('[data-thumbs]');
        if (list) {
          const tr = thumb.getBoundingClientRect(); const lr = list.getBoundingClientRect();
          if (tr.left < lr.left || tr.right > lr.right) list.scrollLeft += tr.left - lr.left - 8;
          if (tr.top < lr.top || tr.bottom > lr.bottom) list.scrollTop += tr.top - lr.top - 8;
        }
      }
      this.slides.forEach((s, n) => { if (n !== index) qsa('video', s).forEach((v) => v.pause()); });
    }
  }
  define('media-gallery', MediaGallery);

  /* ---------- Product info (variants, price, bundles, subscriptions) ---------- */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      const json = qs('[data-product-json]', this);
      if (!json) return;
      try { this.product = JSON.parse(json.textContent); } catch (e) { return; }
      this.form = qs('form[data-main-form]', this);
      this.idInput = qs('[data-variant-id]', this);
      this.planInput = qs('[data-selling-plan]', this);
      this.qtyInput = qs('[data-quantity]', this);
      this.variant = this.product.variants.find((v) => String(v.id) === String(this.idInput?.value)) || this.product.variants[0];
      this.state = { plan: null, qty: 1, discount: 0 };

      // Variant options
      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-option-position]')) this.onOptionChange();
        if (e.target.matches('[data-quantity]')) this.updatePrices();
      });

      // Initial states from pre-selected bundle / plan
      const breaks = qs('quantity-breaks', this);
      if (breaks) breaks.addEventListener('breaks:change', (e) => { this.state.qty = e.detail.qty; this.state.discount = e.detail.discount; if (this.qtyInput) this.qtyInput.value = e.detail.qty; this.updatePrices(); });
      const sub = qs('subscription-picker', this);
      if (sub) sub.addEventListener('plan:change', (e) => { this.state.plan = e.detail.plan; if (this.planInput) this.planInput.value = e.detail.plan || ''; this.updatePrices(); });

      // Pull initial values set by children
      requestAnimationFrame(() => {
        if (breaks && breaks.current) { this.state.qty = breaks.current.qty; this.state.discount = breaks.current.discount; if (this.qtyInput) this.qtyInput.value = breaks.current.qty; }
        if (sub && sub.current !== undefined) { this.state.plan = sub.current; if (this.planInput) this.planInput.value = sub.current || ''; }
        this.markAvailability();
        this.updatePrices();
      });
    }

    selectedOptions() {
      const opts = [];
      qsa('[data-option-position]:checked', this).forEach((input) => {
        opts[parseInt(input.dataset.optionPosition, 10) - 1] = input.value;
      });
      return opts;
    }

    onOptionChange() {
      const opts = this.selectedOptions();
      qsa('.variant-picker__option', this).forEach((fs, i) => {
        const label = qs('[data-selected-value]', fs);
        if (label && opts[i]) label.textContent = opts[i];
      });
      const variant = this.product.variants.find((v) => v.options.every((o, i) => o === opts[i]));
      this.variant = variant || null;
      this.markAvailability();
      if (!variant) { this.setButton(false, strings.unavailable); return; }
      if (this.idInput) this.idInput.value = variant.id;
      if (this.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      if (variant.featured_media) qs('media-gallery', this)?.goToMedia(variant.featured_media.id);
      qs('subscription-picker', this)?.updateVariant(variant);
      this.updatePrices();
    }

    markAvailability() {
      const opts = this.selectedOptions();
      qsa('[data-option-position]', this).forEach((input) => {
        const pos = parseInt(input.dataset.optionPosition, 10) - 1;
        const test = opts.slice(); test[pos] = input.value;
        const match = this.product.variants.find((v) => v.options.every((o, i) => o === test[i]));
        input.classList.toggle('is-unavailable', !match || !match.available);
      });
    }

    unitPrice() {
      const v = this.variant;
      if (!v) return { price: 0, compare: 0 };
      let price = v.price;
      let compare = v.compare_at_price && v.compare_at_price > v.price ? v.compare_at_price : 0;
      if (this.state.plan && v.selling_plan_allocations) {
        const alloc = v.selling_plan_allocations.find((a) => String(a.selling_plan_id) === String(this.state.plan));
        if (alloc) {
          price = alloc.price;
          compare = Math.max(compare, alloc.compare_at_price || v.price);
          if (compare <= price) compare = 0;
        }
      }
      return { price, compare };
    }

    updatePrices() {
      const v = this.variant;
      if (!v) return;
      const { price, compare } = this.unitPrice();
      const qty = this.state.qty || parseInt(this.qtyInput?.value || '1', 10) || 1;
      const disc = this.state.discount || 0;
      const total = Math.round(price * qty * (100 - disc) / 100);

      // Price block
      const block = qs('[data-price-block]', this);
      if (block) {
        const wrap = qs('[data-price-wrapper]', block);
        const cur = qs('[data-price]', block);
        const cmp = qs('[data-compare-price]', block);
        if (cur) cur.textContent = formatMoney(price);
        if (cmp) {
          const hidden = !(compare > price);
          cmp.hidden = hidden;
          if (!hidden) cmp.innerHTML = formatMoney(compare);
        }
        wrap?.classList.toggle('price--on-sale', compare > price);
        const badge = qs('[data-sale-badge]', block);
        if (badge) {
          const pct = compare > price ? Math.round((compare - price) * 100 / compare) : 0;
          badge.hidden = pct <= 0;
          badge.textContent = (badge.dataset.template || '[percent]% off').replace('[percent]', pct);
        }
      }

      // Bundle offer prices
      qs('quantity-breaks', this)?.updatePrices(price, compare || v.price);

      // Button
      this.setButton(v.available, null, total);
      const sticky = qs('[data-sticky-price]', this);
      if (sticky) sticky.textContent = formatMoney(total);
    }

    setButton(available, label, total) {
      const btn = qs('[data-add-button]', this);
      const stickyBtn = qs('[data-sticky-add]', this);
      if (btn) {
        btn.disabled = !available;
        const l = qs('[data-add-label]', btn);
        if (l) l.textContent = label || (available ? (btn.dataset.label || strings.addToCart) : strings.soldOut);
        const p = qs('[data-button-price]', btn);
        if (p) { p.hidden = !available || total == null; if (total != null) p.textContent = '· ' + formatMoney(total); }
      }
      if (stickyBtn) {
        stickyBtn.disabled = !available;
        stickyBtn.textContent = label || (available ? strings.addToCart : strings.soldOut);
      }
    }
  }
  define('product-info', ProductInfo);

  class VariantPicker extends HTMLElement {}
  define('variant-picker', VariantPicker);

  class QuantityBreaks extends HTMLElement {
    connectedCallback() {
      this.options = qsa('.breaks__option', this);
      this.addEventListener('change', (e) => {
        if (e.target.matches('input[type="radio"]')) this.select(e.target);
      });
      const checked = qs('input[type="radio"]:checked', this) || qs('input[type="radio"]', this);
      if (checked) { checked.checked = true; this.select(checked, true); }
    }
    select(input, silent) {
      this.options.forEach((o) => o.classList.toggle('is-selected', o.contains(input)));
      this.current = { qty: parseInt(input.value, 10) || 1, discount: parseFloat(input.dataset.discount) || 0 };
      if (!silent) this.dispatchEvent(new CustomEvent('breaks:change', { detail: this.current, bubbles: false }));
    }
    updatePrices(unit, compareUnit) {
      this.options.forEach((o) => {
        const input = qs('input', o);
        const qty = parseInt(input.value, 10) || 1;
        const disc = parseFloat(input.dataset.discount) || 0;
        const total = Math.round(unit * qty * (100 - disc) / 100);
        const compare = compareUnit * qty;
        const p = qs('[data-offer-price]', o); if (p) p.textContent = formatMoney(total);
        const c = qs('[data-offer-compare]', o);
        if (c) { c.hidden = !(compare > total); c.textContent = formatMoney(compare); }
      });
    }
  }
  define('quantity-breaks', QuantityBreaks);

  class SubscriptionPicker extends HTMLElement {
    connectedCallback() {
      this.options = qsa('.subscribe__option', this);
      this.addEventListener('change', (e) => {
        if (e.target.matches('input[type="radio"]')) this.select(e.target);
      });
      const checked = qs('input[type="radio"]:checked', this) || qs('input[type="radio"]', this);
      if (checked) { checked.checked = true; this.select(checked, true); }
    }
    select(input, silent) {
      this.options.forEach((o) => o.classList.toggle('is-selected', o.contains(input)));
      this.current = input.value || null;
      if (!silent) this.dispatchEvent(new CustomEvent('plan:change', { detail: { plan: this.current } }));
    }
    updateVariant(variant) {
      const one = qs('[data-one-time-price]', this);
      if (one) one.textContent = formatMoney(variant.price);
      qsa('[data-plan-price]', this).forEach((el) => {
        const alloc = (variant.selling_plan_allocations || []).find((a) => String(a.selling_plan_id) === el.dataset.planPrice);
        el.textContent = alloc ? formatMoney(alloc.price) : '';
      });
    }
  }
  define('subscription-picker', SubscriptionPicker);

  /* ---------- Delivery estimate (real dates per visitor) ---------- */
  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      const cutoff = parseInt(this.dataset.cutoff, 10);
      const processing = parseInt(this.dataset.processing, 10) || 0;
      const min = parseInt(this.dataset.min, 10) || 2;
      const max = Math.max(min, parseInt(this.dataset.max, 10) || min);
      const skipWeekends = this.dataset.skipWeekends === 'true';
      const locale = this.dataset.locale || 'en';
      const isBusiness = (d) => !skipWeekends || (d.getDay() !== 0 && d.getDay() !== 6);
      const addBusiness = (date, days) => {
        const d = new Date(date);
        let added = 0;
        while (added < days) { d.setDate(d.getDate() + 1); if (isBusiness(d)) added++; }
        return d;
      };

      const now = new Date();
      const beforeCutoff = now.getHours() < cutoff && isBusiness(now);
      let ship = beforeCutoff ? new Date(now) : addBusiness(now, 1);
      if (processing > 0) ship = addBusiness(ship, processing);
      const from = addBusiness(ship, min);
      const to = addBusiness(ship, max);

      const fmt = (d) => d.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
      const set = (sel, text) => { const el = qs(sel, this); if (el) el.textContent = text; };
      set('[data-date-ordered]', fmt(now));
      set('[data-date-shipped]', fmt(ship));
      set('[data-date-delivered]', min === max ? fmt(from) : `${fmt(from)} – ${fmt(to)}`);

      // "Order within X to ship today" — only when it is true
      const tplEl = qs('[data-tpl-cutoff]', this);
      const out = qs('[data-cutoff-text]', this);
      if (tplEl && out && beforeCutoff && processing === 0) {
        const cut = new Date(now); cut.setHours(cutoff, 0, 0, 0);
        const update = () => {
          const diff = Math.max(0, cut - new Date());
          if (diff <= 0) { out.hidden = true; clearInterval(this.timer); return; }
          const h = Math.floor(diff / 3600000); const m = Math.floor((diff % 3600000) / 60000);
          out.innerHTML = tplEl.innerHTML.replace('[time]', `<strong>${h}h ${m}m</strong>`);
          out.hidden = false;
        };
        update();
        this.timer = setInterval(update, 30000);
      }
    }
    disconnectedCallback() { clearInterval(this.timer); }
  }
  define('delivery-estimate', DeliveryEstimate);

  /* ---------- Sticky add to cart ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const form = document.getElementById(this.dataset.form);
      const mainBtn = form ? qs('[data-add-button]', form) : null;
      if (!form || !mainBtn) return;
      this.hidden = false;
      let ticking = false;
      const update = () => {
        ticking = false;
        const past = mainBtn.getBoundingClientRect().bottom < 0;
        const footer = qs('.footer');
        const nearFooter = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
        this.classList.toggle('is-visible', past && !nearFooter);
      };
      window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
      update();
      qs('[data-sticky-add]', this)?.addEventListener('click', () => {
        if (form.requestSubmit) form.requestSubmit(mainBtn); else mainBtn.click();
      });
    }
  }
  define('sticky-atc', StickyAtc);

  /* ---------- Read more ---------- */
  class ReadMore extends HTMLElement {
    connectedCallback() {
      const content = qs('[data-content]', this);
      const toggle = qs('[data-toggle]', this);
      requestAnimationFrame(() => {
        if (content.scrollHeight <= content.clientHeight + 4) { this.classList.add('is-short'); return; }
        toggle.hidden = false;
        const moreText = toggle.textContent;
        toggle.addEventListener('click', () => {
          const open = this.classList.toggle('is-open');
          toggle.textContent = open ? (toggle.dataset.less || moreText) : moreText;
        });
      });
    }
  }
  define('read-more', ReadMore);

  /* ---------- Count up ---------- */
  class CountUp extends HTMLElement {
    connectedCallback() {
      const target = parseFloat(this.dataset.target);
      if (isNaN(target) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const decimals = (String(this.dataset.target).split('.')[1] || '').length;
      this.textContent = '0';
      const io = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        const start = performance.now(); const dur = 1400;
        const step = (t) => {
          const p = Math.min(1, (t - start) / dur);
          const eased = 1 - Math.pow(1 - p, 3);
          this.textContent = (target * eased).toFixed(decimals);
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }, { threshold: .4 });
      io.observe(this);
    }
  }
  define('count-up', CountUp);

  /* ---------- Before / after ---------- */
  class BeforeAfter extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-ba]');
        if (!btn) return;
        this.dataset.state = btn.dataset.ba;
        qsa('[data-ba]', this).forEach((b) => b.setAttribute('aria-pressed', b === btn));
      });
    }
  }
  define('before-after', BeforeAfter);

  /* ---------- Timeline steps ---------- */
  class ScrollSteps extends HTMLElement {
    connectedCallback() {
      this.steps = qsa('[data-step]', this);
      if (!this.steps.length) return;
      const onScroll = () => {
        const line = window.innerHeight * 0.62;
        let active = 0;
        this.steps.forEach((s, i) => { if (s.getBoundingClientRect().top < line) active = i; });
        this.steps.forEach((s, i) => s.classList.toggle('is-active', i <= active));
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }
  }
  define('scroll-steps', ScrollSteps);

  /* ---------- Simple slider (testimonials) ---------- */
  class SimpleSlider extends HTMLElement {
    connectedCallback() {
      this.slides = qsa('[data-slide]', this);
      this.index = 0;
      this.counter = qs('[data-current]', this);
      if (this.slides.length < 2) return;
      qs('[data-prev]', this)?.addEventListener('click', () => { this.go(this.index - 1); this.restart(); });
      qs('[data-next]', this)?.addEventListener('click', () => { this.go(this.index + 1); this.restart(); });
      this.delay = (parseInt(this.dataset.autoplay, 10) || 0) * 1000;
      this.restart();
      let startX = null;
      this.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      this.addEventListener('touchend', (e) => {
        if (startX == null) return;
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 40) { this.go(this.index + (dx < 0 ? 1 : -1)); this.restart(); }
        startX = null;
      });
    }
    restart() {
      clearInterval(this.timer);
      if (this.delay > 0) this.timer = setInterval(() => this.go(this.index + 1), this.delay);
    }
    go(i) {
      this.index = (i + this.slides.length) % this.slides.length;
      this.slides.forEach((s, n) => s.classList.toggle('is-active', n === this.index));
      if (this.counter) this.counter.textContent = this.index + 1;
    }
  }
  define('simple-slider', SimpleSlider);

  /* ---------- Video player ---------- */
  class VideoPlayer extends HTMLElement {
    connectedCallback() {
      const video = qs('video', this);
      const btn = qs('[data-play]', this);
      if (!video) return;
      video.removeAttribute('controls');
      const toggle = () => {
        if (video.paused) { video.play(); this.classList.add('is-playing'); video.setAttribute('controls', ''); }
        else { video.pause(); this.classList.remove('is-playing'); }
      };
      btn?.addEventListener('click', toggle);
      video.addEventListener('pause', () => this.classList.remove('is-playing'));
      video.addEventListener('play', () => this.classList.add('is-playing'));
    }
  }
  define('video-player', VideoPlayer);

  /* ---------- Share ---------- */
  class ShareButton extends HTMLElement {
    connectedCallback() {
      this.setAttribute('role', 'button');
      this.tabIndex = 0;
      const share = async () => {
        const url = this.dataset.url; const title = this.dataset.title;
        if (navigator.share) { try { await navigator.share({ url, title }); } catch (e) { /* cancelled */ } }
        else if (navigator.clipboard) { await navigator.clipboard.writeText(url); toast('Link copied'); }
      };
      this.addEventListener('click', share);
      this.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); share(); } });
    }
  }
  define('share-button', ShareButton);

  /* ---------- Product recommendations ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      const io = new IntersectionObserver(async ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        try {
          const res = await fetch(this.dataset.url);
          const doc = parseHTML(await res.text());
          const fresh = qs('product-recommendations', doc);
          if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
        } catch (e) { /* ignore */ }
      }, { rootMargin: '0px 0px 400px 0px' });
      io.observe(this);
    }
  }
  define('product-recommendations', ProductRecommendations);

  /* ---------- Free shipping bar (multi-currency) ---------- */
  class FreeShippingBar extends HTMLElement {
    connectedCallback() {
      const rate = (window.Shopify && Shopify.currency && parseFloat(Shopify.currency.rate)) || 1;
      if (rate === 1) return;
      const threshold = Math.round(parseInt(this.dataset.threshold, 10) * rate);
      const total = parseInt(this.dataset.total, 10);
      const remaining = threshold - total;
      const fill = qs('[data-free-shipping-fill]', this);
      const text = qs('[data-free-shipping-text]', this);
      const icon = text ? (qs('svg', text)?.outerHTML || '') : '';
      if (fill) { fill.style.width = Math.min(100, total * 100 / threshold) + '%'; fill.classList.toggle('is-complete', remaining <= 0); }
      if (text) text.innerHTML = icon + ' ' + (remaining <= 0 ? this.dataset.success : this.dataset.text.replace('[amount]', formatMoney(remaining)));
    }
  }
  define('free-shipping-bar', FreeShippingBar);

  /* ---------- Collection filters ---------- */
  class CollectionFilters extends HTMLElement {
    connectedCallback() {
      const form = qs('[data-filters-form]', this);
      if (!form) return;
      const submit = () => {
        const params = new URLSearchParams();
        for (const [k, v] of new FormData(form).entries()) if (v !== '') params.append(k, v);
        window.location.search = params.toString();
      };
      form.addEventListener('change', debounce(submit, 400));
      form.addEventListener('submit', (e) => { e.preventDefault(); submit(); });
      document.addEventListener('click', (e) => {
        qsa('details.filter-dropdown[open]', this).forEach((d) => { if (!d.contains(e.target)) d.open = false; });
      });
    }
  }
  define('collection-filters', CollectionFilters);

  /* ---------- Misc ---------- */
  document.addEventListener('change', (e) => {
    if (e.target.matches('.js-localization-select')) e.target.form?.submit();
  });

  // Customer login: toggle recover password
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-toggle-recover]');
    if (!t) return;
    e.preventDefault();
    const rec = qs('#RecoverPassword'); const login = qs('#CustomerLogin');
    if (rec && login) { rec.hidden = !rec.hidden; login.hidden = !login.hidden; }
  });
  if (window.location.hash === '#recover') {
    const rec = qs('#RecoverPassword'); const login = qs('#CustomerLogin');
    if (rec && login) { rec.hidden = false; login.hidden = true; }
  }

  // Address country default values
  qsa('select[data-default]').forEach((s) => { if (s.dataset.default) s.value = s.dataset.default; });

  // Reveal on scroll
  const initReveal = () => {
    const els = qsa('.reveal:not(.is-visible)');
    if (!('IntersectionObserver' in window) || !document.body.classList.contains('has-animations')) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const siblings = el.parentElement ? Array.from(el.parentElement.children).filter((c) => c.classList.contains('reveal')) : [];
        const i = Math.max(0, siblings.indexOf(el));
        el.style.transitionDelay = Math.min(i, 6) * 70 + 'ms';
        el.classList.add('is-visible');
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: .08 });
    els.forEach((el) => io.observe(el));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initReveal); else initReveal();

  // Theme editor support
  document.addEventListener('shopify:section:load', () => { initReveal(); });
  document.addEventListener('shopify:block:select', (e) => {
    const el = e.target;
    const panel = el.closest && el.closest('[role="tabpanel"]');
    if (el.matches && el.matches('[role="tab"]')) el.click();
    if (el.matches && el.matches('[data-slide]')) {
      const slider = el.closest('simple-slider, announcement-bar');
      if (slider && slider.go) slider.go(slider.slides.indexOf(el));
    }
    if (panel) {
      const tab = document.getElementById(panel.getAttribute('aria-labelledby'));
      if (tab) tab.click();
    }
  });
  document.addEventListener('shopify:section:select', (e) => {
    if (e.target.querySelector && e.target.querySelector('cart-drawer')) qs('cart-drawer')?.open();
  });
})();
